import type { EngineBand, TacticalStatus } from "../lessons/schema.ts";
import { positionKey } from "./positionKey.ts";

export class AnalysisCancelledError extends Error {
  constructor(message = "Analysis cancelled") {
    super(message);
    this.name = "AnalysisCancelledError";
  }
}

export type CandidateEval = {
  uci: string;
  scoreCp: number | null;
  mate: number | null;
  pv: string[];
};

export type EngineAnalyzeFn = (
  fen: string,
  opts: { depth: number; signal?: AbortSignal; candidates?: string[] }
) => Promise<{
  bestmove?: string | null;
  info?: { depth?: number; scoreCp?: number; mate?: number | null; pv?: string | string[] };
  candidates?: CandidateEval[];
}>;

export type AnalysisRequest = {
  fen: string;
  sessionId: string;
  turnId: string;
  depth: number;
  candidateUcis?: string[];
  engineVersion?: string;
};

export type AnalysisKey = {
  positionKey: string;
  candidateUcis: string[];
  depth: number;
  engineVersion: string;
};

export type AnalysisResult = {
  positionKey: string;
  depth: number;
  engineVersion: string;
  bestmove: string | null;
  candidates: CandidateEval[];
  info: { depth?: number; scoreCp?: number; mate?: number | null; pv?: string };
};

function asError(reason: unknown): Error {
  if (reason instanceof Error) return reason;
  return new AnalysisCancelledError(String(reason || "cancelled"));
}

export function engineBandForLoss(cpLoss: number | undefined): EngineBand {
  if (cpLoss == null || Number.isNaN(cpLoss)) return "unknown";
  if (cpLoss <= 15) return "top";
  if (cpLoss <= 40) return "equivalent";
  if (cpLoss <= 90) return "playable";
  return "inferior";
}

export function tacticalStatusForLoss(
  cpLoss: number | undefined | null,
  mateAfter?: number | null,
  mateBefore?: number | null,
  materialDrop?: number | null
): TacticalStatus {
  if (mateAfter != null && mateAfter < 0) return "losing";
  if (mateBefore != null && mateBefore > 0 && (mateAfter == null || mateAfter <= 0)) {
    return "losing";
  }
  if (materialDrop != null && materialDrop >= 200) return "losing";
  if (cpLoss == null || Number.isNaN(cpLoss)) return "unknown";
  if (cpLoss >= 200) return "losing";
  if (cpLoss >= 90) return "questionable";
  return "safe";
}

function normalizePv(pv: string | string[] | undefined): string[] {
  if (!pv) return [];
  if (Array.isArray(pv)) return pv;
  return pv.split(/\s+/).filter(Boolean);
}

export class AnalysisCoordinator {
  private cache = new Map<string, AnalysisResult>();
  private inflight = new Map<string, Promise<AnalysisResult>>();
  private sessionControllers = new Map<string, Set<AbortController>>();
  private ranker: unknown = null;
  private rankerLoading: Promise<unknown> | null = null;

  constructor(
    private readonly deps: {
      analyze: EngineAnalyzeFn;
      timeoutMs?: number;
      engineVersion?: string;
      loadRanker?: () => Promise<unknown>;
    }
  ) {}

  analysisKey(request: AnalysisRequest): AnalysisKey {
    return {
      positionKey: positionKey(request.fen),
      candidateUcis: [...(request.candidateUcis || [])].sort(),
      depth: request.depth,
      engineVersion: request.engineVersion || this.deps.engineVersion || "stockfish",
    };
  }

  cacheKeyFrom(key: AnalysisKey): string {
    return `${key.positionKey}|d${key.depth}|${key.engineVersion}|${key.candidateUcis.join(",")}`;
  }

  getCached(key: AnalysisKey): AnalysisResult | undefined {
    return this.cache.get(this.cacheKeyFrom(key));
  }

  cancelSession(sessionId: string): void {
    const set = this.sessionControllers.get(sessionId);
    if (!set) return;
    for (const controller of set) {
      try {
        controller.abort("session-cancelled");
      } catch {
        /* noop */
      }
    }
    set.clear();
    this.sessionControllers.delete(sessionId);
  }

  async getRanker(): Promise<unknown> {
    if (this.ranker) return this.ranker;
    if (!this.deps.loadRanker) return null;
    if (!this.rankerLoading) this.rankerLoading = this.deps.loadRanker();
    this.ranker = await this.rankerLoading;
    return this.ranker;
  }

  analyze(request: AnalysisRequest, signal?: AbortSignal): Promise<AnalysisResult> {
    const key = this.analysisKey(request);
    const cacheKey = this.cacheKeyFrom(key);
    const cached = this.cache.get(cacheKey);
    if (cached) return Promise.resolve(cached);

    const inflightKey = `${request.sessionId}|${cacheKey}`;
    const existing = this.inflight.get(inflightKey);
    if (existing) {
      return this.follow(existing, signal);
    }

    const controller = new AbortController();
    this.track(request.sessionId, controller);
    const onOuterAbort = () => controller.abort(signal?.reason || "aborted");
    if (signal) {
      if (signal.aborted) controller.abort(signal.reason);
      else signal.addEventListener("abort", onOuterAbort, { once: true });
    }

    const timeoutMs = this.deps.timeoutMs ?? 8000;
    const timer = setTimeout(() => controller.abort("timeout"), timeoutMs);

    const promise = (async () => {
      try {
        if (controller.signal.aborted) throw asError(controller.signal.reason);
        const raw = await this.deps.analyze(request.fen, {
          depth: request.depth,
          signal: controller.signal,
          ...(request.candidateUcis?.length ? { candidates: request.candidateUcis } : {}),
        });
        if (controller.signal.aborted) throw asError(controller.signal.reason);
        const result = this.normalize(raw, key);
        this.cache.set(cacheKey, result);
        return result;
      } catch (err) {
        if (controller.signal.aborted) throw asError(controller.signal.reason);
        throw err;
      } finally {
        clearTimeout(timer);
        if (signal) signal.removeEventListener("abort", onOuterAbort);
        this.untrack(request.sessionId, controller);
        this.inflight.delete(inflightKey);
      }
    })();

    this.inflight.set(inflightKey, promise);
    return promise;
  }

  private follow(promise: Promise<AnalysisResult>, signal?: AbortSignal): Promise<AnalysisResult> {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(asError(signal.reason));
    return new Promise((resolve, reject) => {
      const onAbort = () => reject(asError(signal.reason));
      signal.addEventListener("abort", onAbort, { once: true });
      promise.then(
        (value) => {
          signal.removeEventListener("abort", onAbort);
          resolve(value);
        },
        (err) => {
          signal.removeEventListener("abort", onAbort);
          reject(err);
        }
      );
    });
  }

  private track(sessionId: string, controller: AbortController): void {
    let set = this.sessionControllers.get(sessionId);
    if (!set) {
      set = new Set();
      this.sessionControllers.set(sessionId, set);
    }
    set.add(controller);
  }

  private untrack(sessionId: string, controller: AbortController): void {
    const set = this.sessionControllers.get(sessionId);
    if (!set) return;
    set.delete(controller);
    if (set.size === 0) this.sessionControllers.delete(sessionId);
  }

  private normalize(raw: Awaited<ReturnType<EngineAnalyzeFn>>, key: AnalysisKey): AnalysisResult {
    const pv = normalizePv(raw.info?.pv);
    const bestmove = raw.bestmove || pv[0] || null;
    const candidates =
      raw.candidates && raw.candidates.length
        ? raw.candidates
        : bestmove
          ? [
              {
                uci: bestmove,
                scoreCp: raw.info?.scoreCp ?? null,
                mate: raw.info?.mate ?? null,
                pv,
              },
            ]
          : [];
    return {
      positionKey: key.positionKey,
      depth: raw.info?.depth ?? key.depth,
      engineVersion: key.engineVersion,
      bestmove,
      candidates,
      info: {
        depth: raw.info?.depth ?? key.depth,
        scoreCp: raw.info?.scoreCp,
        mate: raw.info?.mate ?? null,
        pv: pv.join(" "),
      },
    };
  }
}
